// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { type ChartOptions, type Plugin, type TooltipItem, type ChartDataset, type Chart, type LegendItem } from 'chart.js/auto';
import { type BarChartVariantType, type ReportDataset, type ReportData, type ChartAnnotationType } from './_chart';
import { type EventContext } from 'chartjs-plugin-annotation';

export type GetBarChartOptionsProps = {
    variant: BarChartVariantType;
    maxValue?: number;
    showFilterTitle?: boolean;
    annotation?: ChartAnnotationType;
    xLabel?: string;
    yLabel?: string;
};

export type GetLineChartOptionsProps = {
    maxValue?: number;
    showFilterTitle?: boolean;
    annotation?: ChartAnnotationType;
    xLabel?: string;
    yLabel?: string;
};

const CHART_OPTIONS_BASE = (showFilterTitle?: boolean): ChartOptions => {
    return {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    filter: (item, data): boolean => {
                        const datasetIndex = item.datasetIndex ?? '0';
                        const dataset = data.datasets[datasetIndex] as ReportDataset;

                        return !!(item.text) && !dataset.disableFilter;
                    },
                    font: { size: 16 },
                    color: '#5B6770',
                    usePointStyle: true,
                },
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onClick: (e: any, legendItem: LegendItem, legend: any): void => {
                    const index = legendItem.datasetIndex ?? 0;
                    const ci = legend.chart;
                    const itemCount = legend.legendItems?.length ?? 0;
                    let isAllShowing = true;
                    let isAllOtherHidden = true;

                    const allDatasets = legend.chart.data.datasets || [];

                    for (let i=0; i<itemCount; i += 1) {
                        const dataIndex = legend.legendItems[i].datasetIndex;
                        if (!ci.isDatasetVisible(dataIndex)) {
                            isAllShowing = false;
                        } else if (dataIndex !== index) {
                            isAllOtherHidden = false;
                        }
                    }
                    if (isAllShowing) {
                        for (let i=0; i<itemCount; i += 1) {
                            const dataIndex = legend.legendItems[i].datasetIndex;
                            if (dataIndex !== index) {
                                if (!allDatasets[dataIndex].disableFilter) {
                                    ci.hide(dataIndex);
                                }
                            }
                        }
                    } else if (isAllOtherHidden) {
                        for (let i=0; i<itemCount; i += 1) {
                            const dataIndex = legend.legendItems[i].datasetIndex;
                            if (dataIndex !== index) {
                                ci.show(dataIndex);
                            }
                        }
                    } else if (ci.isDatasetVisible(index)) {
                        ci.hide(index);
                    } else {
                        ci.show(index);
                    }
                },
                title: {
                    display: !!(showFilterTitle),
                    text: '  Filter:',
                    position: 'start',
                    color: '#373E43',
                    font: {
                        family: '"Source Sans Pro", system-ui, -apple-system, blinkmacsystemfont, "Segoe UI", roboto, "Helvetica Neue", arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                        size: 18,
                    },
                },
                align: 'start',
            },
        },
        parsing: {
            xAxisKey: 'labelText',
            yAxisKey: 'value',
        },
    };
};

const CHART_OPTIONS_BAR_BASE = (showFilterTitle?: boolean): ChartOptions<'bar'> => {
    const baseOptions = CHART_OPTIONS_BASE(showFilterTitle);
    return {
        ...baseOptions,
        plugins: {
            ...baseOptions.plugins,
            tooltip: {
                callbacks: {
                    label(tooltipItem: TooltipItem<'bar'>): string {
                        const datasetLabel = tooltipItem.dataset.label;
                        const value = (Number((tooltipItem.raw as ReportData).value) || 0).toFixed(2);
                        return `${datasetLabel}: ${value}`;
                    },
                },
            },
        },
        maxBarThickness: 28,
        borderRadius: 600,
    } as unknown as ChartOptions<'bar'>;
};

const CHART_OPTIONS_LINE_BASE = (showFilterTitle?: boolean): ChartOptions<'line'> => {
    const baseOptions = CHART_OPTIONS_BASE(showFilterTitle);
    return {
        ...baseOptions,
        plugins: {
            ...baseOptions.plugins,
            tooltip: {
                callbacks: {
                    label(tooltipItem: TooltipItem<'line'>): string {
                        const datasetLabel = tooltipItem.dataset.label;
                        const value = (Number((tooltipItem.raw as ReportData).value) || 0).toFixed(2);
                        return `${datasetLabel}: ${value}`;
                    },
                },
            },
        },
    } as ChartOptions<'line'>;
};

const PLUGIN_GENERATE_LEGEND_LABEL = (chart: Chart<'bar'>): LegendItem[] => {
    const result = chart.data?.datasets.map((dataset: ChartDataset, index: number) => {
        let colorToUse = dataset.backgroundColor === 'transparent' ? dataset.borderColor : dataset.backgroundColor;

        const getNonTransparentColor = (colors: [string]): string | undefined => {
            return colors.find((colorvalue): boolean => {
                return colorvalue !== 'transparent';
            });
        };

        let nonTransparentBorder = '';
        let nonTransparentFill = '';


        if (Array.isArray(dataset.borderColor)) {
            nonTransparentBorder = getNonTransparentColor(dataset.borderColor as [string]) || '';
        }
        if (Array.isArray(dataset.backgroundColor)) {
            nonTransparentFill = getNonTransparentColor(dataset.backgroundColor as [string]) || '';
        }

        if (nonTransparentBorder || nonTransparentFill) {
            colorToUse = nonTransparentFill ? nonTransparentFill : nonTransparentBorder;
        }

        return {
            datasetIndex: index,
            text: dataset.label,
            fillStyle: colorToUse,
            strokeStyle: colorToUse,
            hidden: !chart.isDatasetVisible(index),
        } as LegendItem;
    });

    return result;
};

const generateChartAxesTicks = (preferredFontSize?: number): { ticks: { font: { size: number }}} => {
    const fontSize = preferredFontSize ?? 14;
    return { ticks: { font: { size: fontSize } } };
};

const generateChartAxesLabel = (label?: string): unknown => {
    if (!label) {
        return undefined;
    }

    return {
        display: true,
        text: label,
        font: {
            size: 18,
            weight: 'bold',
            lineHeight: 1.2,
        },
    };
};

const CHART_OPTIONS_BAR_VARIANT_1 = (max?: number, xLabel?: string, yLabel?: string): ChartOptions<'bar'> => {
    const ticks = generateChartAxesTicks(20);
    return {
        scales: {
            x: {
                stacked: true,
                title: generateChartAxesLabel(xLabel),
                ...ticks,
            },
            y: {
                suggestedMin: 0,
                suggestedMax: max ? max : undefined,
                title: generateChartAxesLabel(yLabel),
                ...ticks,
            },
        },
        borderSkipped: false,
        plugins: { legend: { labels: { generateLabels: PLUGIN_GENERATE_LEGEND_LABEL } } },
    } as unknown as ChartOptions<'bar'>;
};

const CHART_OPTIONS_BAR_VARIANT_2 = (max?: number, xLabel?: string, yLabel?: string): ChartOptions<'bar'> => {
    const ticks = generateChartAxesTicks(20);
    return {
        scales: {
            x: {
                stacked: false,
                title: generateChartAxesLabel(xLabel),
                ...ticks,
            },
            y: {
                suggestedMin: 0,
                suggestedMax: max ? max : undefined,
                title: generateChartAxesLabel(yLabel),
                ...ticks,
            },
        },
        borderSkipped: false,
        plugins: { legend: { labels: { generateLabels: PLUGIN_GENERATE_LEGEND_LABEL } } },
    } as unknown as ChartOptions<'bar'>;
};

const CHART_OPTIONS_BAR_VARIANT_3 = (max?: number, xLabel?: string, yLabel?: string): ChartOptions<'bar'> => {
    const ticks = generateChartAxesTicks(20);
    return {
        scales: {
            x: {
                stacked: true,
                title: generateChartAxesLabel(xLabel),
                ...ticks,
            },
            y: {
                suggestedMin: 0,
                suggestedMax: max ? max : undefined,
                stacked: true,
                title: generateChartAxesLabel(yLabel),
                ...ticks,
            },
        },
        plugins: { legend: { labels: { generateLabels: PLUGIN_GENERATE_LEGEND_LABEL } } },
    } as unknown as ChartOptions<'bar'>;
};

const CHART_OPTIONS_BAR_VARIANT_4 = (max?: number, xLabel?: string, yLabel?: string): ChartOptions<'bar'> => {
    const ticks = generateChartAxesTicks(20);
    return {
        scales: {
            x: {
                stacked: true,
                title: generateChartAxesLabel(xLabel),
                ...ticks,
            },
            y: {
                suggestedMin: 0,
                suggestedMax: max ? max : undefined,
                stacked: false,
                title: generateChartAxesLabel(yLabel),
                ...ticks,
            },
        },
        borderSkipped: false,
        interaction: { mode: 'index' },
        plugins: { legend: { labels: { generateLabels: PLUGIN_GENERATE_LEGEND_LABEL } } },
    } as unknown as ChartOptions<'bar'>;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const GetChartAnnotation = (annotation?: ChartAnnotationType): any => {
    if (annotation) {
        const {
            value,
            color = '#7B848C', // black color for default
            content,
        } = annotation;

        if (value) {
            return {
                annotation: {
                    annotations: [
                        {
                            type: 'line',
                            yMin: value,
                            yMax: value,
                            borderColor: color,
                            borderWidth: 3,
                            borderDash: [6, 6],
                            borderDashOffset: 0,
                            ...(content // only renders label (and show / hide it) if content is supplied.
                                ? {
                                    label: {
                                        display: false,
                                        backgroundColor: color,
                                        drawTime: 'afterDatasetsDraw',
                                        content,
                                    },
                                    // EventContext
                                    enter: ({ element }: EventContext): boolean => {
                                        if (element.label) {
                                            element.label.options.display = true;
                                        }
                                        return true; // force update
                                    },
                                    leave: ({ element }: EventContext): boolean => {
                                        if (element.label) {
                                            element.label.options.display = false;
                                        }
                                        return true;
                                    },
                                }
                                : {}
                            ),
                        }
                    ],
                },
            };
        }
    }

    return {};
};


export const GetBarChartOptions = (props: GetBarChartOptionsProps): ChartOptions<'bar'> => {
    const {
        variant,
        maxValue,
        showFilterTitle,
        annotation,
        xLabel,
        yLabel,
    } = props;

    const baseOption = CHART_OPTIONS_BAR_BASE(showFilterTitle);
    let variantOptions: ChartOptions<'bar'> = {};

    if (variant === '1') {
        variantOptions = CHART_OPTIONS_BAR_VARIANT_1(maxValue, xLabel, yLabel);
    } else if (variant === '2') {
        variantOptions = CHART_OPTIONS_BAR_VARIANT_2(maxValue, xLabel, yLabel);
    } else if (variant === '3') {
        variantOptions = CHART_OPTIONS_BAR_VARIANT_3(maxValue, xLabel, yLabel);
    } else if (variant === '4') {
        variantOptions = CHART_OPTIONS_BAR_VARIANT_4(maxValue, xLabel, yLabel);
    }

    const chartAnnotation = GetChartAnnotation(annotation);

    return {
        ...baseOption,
        ...variantOptions,
        plugins: {
            ...baseOption.plugins,
            legend: {
                ...baseOption.plugins?.legend,
                labels: {
                    ...baseOption.plugins?.legend?.labels,
                    ...(variantOptions.plugins ? variantOptions.plugins.legend?.labels : {}),
                },
            },
            ...chartAnnotation,
        },

    };
};

export const GetLineChartOptions = (props: GetLineChartOptionsProps): ChartOptions<'line'> => {
    const {
        maxValue,
        showFilterTitle,
        annotation,
        xLabel,
        yLabel,
    } = props;
    const baseOption = CHART_OPTIONS_LINE_BASE(showFilterTitle);

    const chartAnnotation = GetChartAnnotation(annotation);
    const ticks = generateChartAxesTicks(20);
    return {
        ...baseOption,
        scales: {
            x: {
                stacked: true,
                title: generateChartAxesLabel(xLabel),
                ...ticks,
            },
            y: {
                suggestedMin: 0,
                suggestedMax: maxValue ? maxValue : undefined,
                title: generateChartAxesLabel(yLabel),
                ...ticks,
            },
        },
        plugins: {
            ...baseOption.plugins,
            legend: {
                ...baseOption.plugins?.legend,
                labels: { ...baseOption.plugins?.legend?.labels },
            },
            ...chartAnnotation,
        },
    } as unknown as ChartOptions<'line'>;
};

export const ChartPluginLegendMargin = (modifierValue: number): Plugin => {
    return {
        id: 'ChartPluginLegendMargin',
        beforeInit: (chart: Chart): void => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const fitValue = chart.legend?.fit;

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            chart.legend.fit = function fit(): number {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                fitValue.bind(chart.legend)();
                if (this) {
                    this.height += modifierValue;
                    return this.height;
                }

                return modifierValue;
                // return this.height += modifierValue; // eslint-disable-line no-return-assign
            };
        },
        // @NOTE - the below code can be used to set default visible data
        afterInit: (chart: Chart): void => {
            const allDatasets = chart.data.datasets || [];
            allDatasets.forEach((dataset, datasetIndex) => {
                if ((dataset as ReportDataset).invisible === true) {
                    chart.setDatasetVisibility(datasetIndex, false);
                }
            });
        },
    };
};
